/**
 * 🚨 403 ERROR PAGE
 * Professional "Access Forbidden" component
 * Handles authorization and permission errors
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as styles from './errorPages.css';

interface ForbiddenPageProps {
  /** Optional custom message */
  message?: string;
  /** Required permission/role */
  requiredPermission?: string;
  /** Show login option */
  showLoginOption?: boolean;
  /** Custom login redirect */
  loginPath?: string;
}

const ForbiddenPage: React.FC<ForbiddenPageProps> = ({
  message,
  requiredPermission,
  showLoginOption = true,
  loginPath = '/admin/login'
}) => {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = React.useState(false);

  const handleGoBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };

  const suggestions = [
    'Vérifiez que vous êtes connecté avec le bon compte',
    'Contactez l\'administrateur pour obtenir les permissions nécessaires',
    'Retournez à une section publique du portfolio',
    'Déconnectez-vous et reconnectez-vous avec un compte autorisé'
  ];

  return (
    <div className={styles.errorContainer}>
      {/* Breadcrumb for context */}
      <nav className={styles.breadcrumb}>
        <Link to="/" className={styles.breadcrumbLink}>Accueil</Link>
        <span> / </span>
        <span>Erreur 403</span>
      </nav>

      <div className={styles.errorContent}>
        {/* Error Icon */}
        <div className={styles.errorIcon}>
          🔒
        </div>

        {/* Error Code */}
        <h1 className={styles.errorCode}>
          403
          <span className={styles.srOnly}>Erreur quatre cent trois</span>
        </h1>

        {/* Error Title */}
        <h2 className={styles.errorTitle}>
          Accès Interdit
        </h2>

        {/* Error description */}
        <p className={styles.errorDescription}>
          {message || 
            "Vous n'avez pas les permissions nécessaires pour accéder à cette ressource. Cette section est réservée aux utilisateurs autorisés."
          }
        </p>

        {/* permission Info */}
        {requiredPermission && (
          <div style={{
            padding: '1rem',
            backgroundColor: 'var(--colors-warning-bg)',
            borderRadius: '8px',
            border: '1px solid var(--colors-warning-border)',
            marginBottom: '2rem'
          }}>
            <strong>Permission requise :</strong> {requiredPermission}
          </div>
        )}

        {/* Suggestions */}
        <div>
          <h3 style={{ 
            fontSize: '1.1rem', 
            marginBottom: '1rem', 
            color: 'var(--colors-text)',
            fontWeight: 600 
          }}>
            Que pouvez-vous faire ?
          </h3>
          <ul className={styles.suggestionsList}>
            {suggestions.map((suggestion, index) => (
              <li key={index} className={styles.suggestionItem}>
                {suggestion}
              </li>
            ))}
          </ul>
        </div>

        {/* action Buttons */}
        <div className={styles.actionButtons}>
          {showLoginOption && (
            <Link to={loginPath} className={styles.primaryButton}>
              <span>🔑</span>
              Se connecter
            </Link>
          )}
          <Link to="/" className={styles.secondaryButton}>
            <span>🏠</span>
            Retour à l'accueil
          </Link>
          <button 
            onClick={handleGoBack}
            className={styles.secondaryButton}
          >
            <span>⬅️</span>
            Page précédente
          </button>
        </div>

        {/* Status Indicator */}
        <div className={styles.statusIndicator}>
          <div className={styles.statusDot}></div>
          <span>Système de sécurité actif</span>
        </div>

        {/* Technical Details */}
        <div className={styles.technicalDetails}>
          <h4 
            className={styles.technicalTitle}
            onClick={() => setShowDetails(!showDetails)}
          >
            <span>{showDetails ? '🔽' : '▶️'}</span>
            Informations de sécurité
          </h4>
          {showDetails && (
            <div className={styles.technicalContent}>
              {`Erreur: 403 Forbidden
Ressource: ${window.location.pathname}
Permission requise: ${requiredPermission || 'Non spécifiée'}
Timestamp: ${new Date().toISOString()}
Session: ${localStorage.getItem('userRole') || 'Non authentifié'}
IP: [Masquée pour la sécurité]`}
            </div>
          )}
        </div>

        {/* Help section */}
        <div className={styles.helpSection}>
          <h3 className={styles.helpTitle}>
            Besoin d'accès ?
          </h3>
          <p className={styles.helpText}>
            Si vous pensez avoir besoin d'accéder à cette section ou si vous rencontrez 
            une erreur, contactez l'administrateur du site.
          </p>
          <div className={styles.contactInfo}>
            <a href="mailto:admin@yourportfolio.com" className={styles.contactLink}>
              📧 admin@yourportfolio.com
            </a>
            <span style={{ fontSize: '0.9rem', color: 'var(--colors-text-secondary)' }}>
              🛡️ Toutes les tentatives d'accès sont journalisées
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForbiddenPage;
